/*
 * willoway-toast.js — a small, generic toast/notification system for the
 * Willoway theme.
 *
 * Keystone ships three hard-coded alerts (error/discount/cart) bound to fixed
 * booleans on the root app() state. This adds a programmatic, single-slot
 * alternative so ANY flow can surface transient success / error / info feedback
 * without wiring a new alert slot — used by the pre-order flows (saving a
 * request, uploading the order form, re-adding last year's order).
 *
 * Behaviour:
 *   - SINGLE SLOT: a new toast REPLACES the one currently showing (the latest
 *     notification is the only one on screen), so rapid edits don't stack up.
 *   - AUTO-DISMISS on a timer, with a progress bar across the top that shrinks
 *     right→left; when it reaches zero the toast slides out.
 *
 * Fire one from anywhere:
 *   window.willowayToast('Pre-order saved');                       // info
 *   window.willowayToast({ type: 'success', message: 'Saved',
 *                          description: 'Acer × 12 requested' });
 *   window.dispatchEvent(new CustomEvent('willoway:toast',
 *     { detail: { type: 'error', message: 'Could not save' } }));
 *
 * The container that renders it lives in snippets/theme__toast-overlay.liquid
 * (mounted once in layout/theme.liquid), which listens for the `willoway:toast`
 * window event and calls add().
 */
(function () {
  var EVENT = 'willoway:toast';

  // Convenience global. Accepts either a string message or a full detail object.
  //   willowayToast('hello')
  //   willowayToast('hello', { type: 'success' })
  //   willowayToast({ type: 'error', message: 'x', description: 'y', duration: 8000 })
  window.willowayToast = function (message, opts) {
    var detail =
      message && typeof message === 'object'
        ? message
        : Object.assign({ message: message }, opts || {});
    try {
      window.dispatchEvent(new CustomEvent(EVENT, { detail: detail }));
    } catch (e) {
      /* CustomEvent unsupported — feedback is non-critical, fail silently */
    }
  };

  document.addEventListener('alpine:init', function () {
    Alpine.data('willowayToast', function () {
      return {
        // The single toast currently on screen (content swaps in place when a
        // newer one arrives).
        current: { id: 0, type: 'info', message: '', description: '' },
        visible: false,
        progress: 100, // progress-bar width %, animates 100 -> 0
        _raf: null,
        _seq: 0,
        _duration: 0, // total dwell time for the current toast
        _elapsed: 0, // ms already counted before the running segment
        _segStart: 0, // performance.now() when the running segment began
        _paused: false,
        _hovering: false, // pointer/focus is over the toast → keep it paused

        // Push a toast. `detail` mirrors the willoway:toast event payload. If one
        // is already showing, this replaces it (content + timer + bar reset).
        add: function (detail) {
          detail = detail || {};
          var type = detail.type || 'info';
          var message = detail.message || '';
          var description = detail.description || '';
          if (!message && !description) return;
          // Default dwell time — errors linger longer since they need reading.
          var duration =
            typeof detail.duration === 'number'
              ? detail.duration
              : type === 'error'
              ? 7000
              : 4000;

          this.current = {
            id: ++this._seq,
            type: type,
            message: message,
            description: description,
          };
          this.visible = true;
          this._start(duration);
        },

        // Begin the countdown for a (possibly replacement) toast. If the pointer
        // is already over the toast, start paused so hovering the previous one
        // holds the new one too.
        _start: function (duration) {
          this._cancel();
          this._duration = duration;
          this._elapsed = 0;
          this.progress = 100;
          if (!(duration > 0)) {
            this._paused = false; // 0/negative = sticky (used by demos)
            return;
          }
          if (this._hovering) {
            this._paused = true; // resumes on mouseleave / focusout
          } else {
            this._paused = false;
            this._segStart = performance.now();
            this._loop();
          }
        },

        // rAF loop: progress reflects total elapsed across all running segments,
        // so pausing/resuming just adds another segment.
        _loop: function () {
          var self = this;
          var step = function (now) {
            var total = self._elapsed + (now - self._segStart);
            var pct = 100 - (total / self._duration) * 100;
            self.progress = pct > 0 ? pct : 0;
            if (total >= self._duration) {
              self._raf = null;
              self.dismiss();
              return;
            }
            self._raf = requestAnimationFrame(step);
          };
          this._raf = requestAnimationFrame(step);
        },

        // Pause the timer + bar while hovered or keyboard-focused, so the reader
        // can finish (and reach the close button) without it vanishing.
        pause: function () {
          this._hovering = true;
          if (this._paused || !this.visible || !(this._duration > 0)) return;
          this._cancel();
          this._elapsed += performance.now() - this._segStart;
          this._paused = true;
        },

        resume: function () {
          this._hovering = false;
          if (!this._paused || !this.visible || !(this._duration > 0)) return;
          this._paused = false;
          this._segStart = performance.now();
          this._loop();
        },

        _cancel: function () {
          if (this._raf) {
            cancelAnimationFrame(this._raf);
            this._raf = null;
          }
        },

        dismiss: function () {
          this._cancel();
          this._paused = false;
          this.visible = false;
        },

        // Per-type presentation helpers used by the template.
        accentClass: function (type) {
          if (type === 'success') return 'color__text-success';
          if (type === 'error') return 'color__text-error';
          return 'color__text';
        },
      };
    });
  });
})();
